import React from "react";
import { Container } from "react-bootstrap";
import Header from "../Includes/Header";
import Footer from "../Includes/Footer";
import "../../App.css";

const TermsOfService: React.FC = () => {
  return (
    <>
      <Header />
      <Container className="py-5 mt-4" style={{ maxWidth: 820 }}>
        <h1 className="fw-bold mb-1">Terms of Service</h1>
        <p className="text-secondary small mb-1">Last updated: August 2026</p>
        <p className="text-secondary small mb-5" style={{ lineHeight: 1.7 }}>
          ARM Financial Services Pty Ltd ("ARMF", "we", "us", or "our") · ABN 35 659 595 570 · ACN 659 595 570
          <br />
          Registered with AUSTRAC to provide remittance services in Australia (AAN 100812026, Registered Entity No. 75630)
          <br />
          Suite 110, Level 1, 530 Little Collins Street, Melbourne VIC 3000, Australia
        </p>

        <Section title="1. Acceptance of Terms">
          By accessing or using the ARMF (ARM Financial Services Pty Ltd) onboarding platform,
          you agree to be bound by these Terms of Service and all applicable laws and
          regulations. If you do not agree with any of these terms, you are prohibited from
          using this platform.
        </Section>

        <Section title="2. Eligibility">
          You must be at least 18 years of age to use our services. By using ARMF, you represent
          and warrant that you meet this requirement and that you have the legal capacity to enter
          into a binding agreement. Entity accounts may only be registered by authorised
          representatives of the legal entity.
        </Section>

        <Section title="3. Account Registration">
          To access our services you must complete the onboarding and KYC (Know Your Customer)
          process. You agree to provide accurate, current, and complete information during
          registration and to update such information to keep it accurate, current, and
          complete. ARMF reserves the right to suspend or terminate any account where information
          is found to be inaccurate or incomplete.
        </Section>

        <Section title="4. KYC & AML Compliance">
          <p>
            ARMF is required, under the Anti-Money Laundering and Counter-Terrorism Financing Act
            2006 (Cth) (the AML/CTF Act), to verify the identity of all users before providing our
            services. As part of our onboarding process, you consent to the collection and
            processing of your identity documents, facial biometrics, and related data for the
            purposes of identity verification, anti-money laundering (AML) screening, and fraud
            prevention. We may share this information with authorised third-party verification
            providers and, where required by law, with AUSTRAC.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          You agree not to use the ARMF platform for any unlawful purpose, including but not
          limited to money laundering, terrorist financing, structuring transactions to avoid
          reporting thresholds, fraud, or circumventing sanctions. ARMF reserves the right to
          report suspicious activity to AUSTRAC and other relevant regulatory authorities, and to
          immediately suspend or terminate access to any account involved in prohibited activity.
          Further detail on prohibited uses is set out in our{" "}
          <a href="/acceptable-use-policy">Acceptable Use Policy</a>.
        </Section>

        <Section title="6. Financial Services">
          <p>
            ARMF provides remittance, multi-currency account, and payment services. All services
            are subject to applicable Australian financial regulations. Service availability may
            vary by country. ARMF does not provide investment advice, and no content on this
            platform should be construed as such.
          </p>
          <p className="mb-0">
            <strong>ARMF is not an authorised deposit-taking institution (ADI) and is not
            licensed to accept deposits.</strong> Funds you hold or transact through ARMF are not
            covered by the Financial Claims Scheme. See our{" "}
            <a href="/risk-factors">Risk Factors</a> page for further detail.
          </p>
        </Section>

        <Section title="7. Fees & Charges">
          Certain services offered by ARMF may be subject to fees. All applicable fees will be
          clearly disclosed before you complete a transaction. ARMF reserves the right to change
          its fee structure with reasonable prior notice.
        </Section>

        <Section title="8. Intellectual Property">
          All content, branding, logos, and software on the ARMF platform are the exclusive
          property of ARM Financial Services Pty Ltd or its licensors. You may not copy,
          reproduce, distribute, or create derivative works without prior written consent.
        </Section>

        <Section title="9. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, ARMF shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising from your use of the
            platform or services. Our total liability to you shall not exceed the fees paid by you
            to ARMF in the twelve months preceding the claim.
          </p>
          <p className="mb-0">
            Nothing in these Terms excludes, restricts, or modifies any guarantee, right, or
            remedy under the Australian Consumer Law, or any other law, that cannot lawfully be
            excluded, restricted, or modified.
          </p>
        </Section>

        <Section title="10. Termination">
          ARMF reserves the right to suspend or terminate your access to the platform at any
          time, with or without cause, and with or without notice. Upon termination, your right
          to use the platform will immediately cease.
        </Section>

        <Section title="11. Governing Law">
          These Terms shall be governed by and construed in accordance with the laws of Victoria,
          Australia. You submit to the non-exclusive jurisdiction of the courts of Victoria and
          the courts competent to hear appeals from those courts.
        </Section>

        <Section title="12. Changes to Terms">
          ARMF reserves the right to modify these Terms at any time. We will provide notice of
          significant changes by updating the "Last updated" date at the top of this page.
          Continued use of the platform after changes constitutes your acceptance of the revised
          Terms.
        </Section>

        <Section title="13. Dispute Resolution & Complaints">
          <p>
            If you have a complaint about our services, please contact us first at{" "}
            <a href="mailto:support@armf.com">support@armf.com</a>. We will acknowledge your
            complaint promptly and aim to resolve it within 30 days.
          </p>
          <p className="mb-0">
            If you're not satisfied with our response, ARMF is a member of the{" "}
            <strong>Australian Financial Complaints Authority (AFCA)</strong> (Member No.
            FF157726), which provides a free, independent external dispute resolution service for
            eligible complaints. AFCA can be contacted at{" "}
            <a href="https://www.afca.org.au" target="_blank" rel="noreferrer">afca.org.au</a>,
            on 1800 931 678, or by mail to GPO Box 3, Melbourne VIC 3001.
          </p>
        </Section>

        <Section title="14. Contact Us">
          If you have any questions about these Terms, please contact our support team at{" "}
          <a href="mailto:support@armf.com">support@armf.com</a>.
        </Section>
      </Container>
      <Footer full={true} />
    </>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="fw-semibold mb-2">{title}</h5>
    <div className="text-secondary" style={{ lineHeight: 1.8 }}>{children}</div>
  </div>
);

export default TermsOfService;
